import { useState, useEffect, useRef, useCallback } from "react";
import {
  Clock3,
  History,
  Loader2,
  RotateCcw,
  TriangleAlert,
  UtensilsCrossed,
  CheckCircle2,
  ChevronDown,
  ArrowDownUp,
  Bell,
  BellOff,
} from "lucide-react";
import { toast } from "sonner";
import { usePOS } from "@/context/POSContext";
import { useKdsTickets } from "@/hooks/useKdsTickets";
import { Button } from "@/components/ui/button";
import { fmtQty } from "@/lib/qty";
import { combineItems } from "@/lib/kdsCombine";
import { elapsedFromMinutes } from "@/lib/format";

const ticketTitle = ticket =>
  ticket.table_label || (ticket.table_number ? `Table ${ticket.table_number}` : "Takeaway");

// Variation and modifiers describe what to cook; a note is an instruction from the
// captain and gets its own line so it cannot be lost in a run-on string.
const itemDetail = item => [item.variation_name, ...(item.modifier_names || [])].filter(Boolean).join(" · ");

// Per-screen choices (sort order, sound) live on the device: the kitchen and the bar
// screen may want them differently. Storage can throw in a locked-down browser.
const readChoice = (key, fallback) => {
  try {
    const value = window.localStorage.getItem(key);
    return value === null ? fallback : value;
  } catch {
    return fallback;
  }
};
const saveChoice = (key, value) => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* the choice still holds for this session */
  }
};
const sentAt = ticket => (ticket.kot_sent_at ? Date.parse(ticket.kot_sent_at) : 0);

export default function KDSPage() {
  const { subscribeRealtime } = usePOS();
  const [station, setStation] = useState(null);
  const { tickets: served, boardStation, stations, kdsSettings, bump, recall, error, loading } = useKdsTickets(
    subscribeRealtime,
    station
  );
  // Newest KOT first unless this screen chose otherwise (owner, 2026-11-01).
  const [newestFirst, setNewestFirst] = useState(() => readChoice("kds.sort", "newest") === "newest");
  const tickets = [...served].sort((a, b) => (newestFirst ? sentAt(b) - sentAt(a) : sentAt(a) - sentAt(b)));
  const combinedLines = combineItems(tickets);
  const [combinedCollapsed, setCombinedCollapsed] = useState(
    () => readChoice("kds.combined_collapsed", readChoice("kds_combined_collapsed", "false")) === "true"
  );
  const toggleCombined = () => {
    setCombinedCollapsed(prev => {
      const next = !prev;
      saveChoice("kds.combined_collapsed", String(next));
      saveChoice("kds_combined_collapsed", String(next));
      return next;
    });
  };
  const toggleSort = () => {
    saveChoice("kds.sort", newestFirst ? "oldest" : "newest");
    setNewestFirst(!newestFirst);
  };
  // This screen's own sound switch; the station setting stays the master.
  const [soundOn, setSoundOn] = useState(() => readChoice("kds.sound", "on") === "on");
  // A browser keeps audio blocked until someone taps the page, so the chime could be
  // silent with no sign (owner, 2026-11-01: "enable kot notification"). One context,
  // unlocked by a tap on Sound; while it is still blocked the bar says so.
  const audioRef = useRef(null);
  const [soundBlocked, setSoundBlocked] = useState(false);
  const activeSettings = kdsSettings || {};
  const [lastBumped, setLastBumped] = useState(null); // { ticket, time }
  const announcedTickets = useRef(new Set());
  const announcedStation = useRef(undefined);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  // The chime announces a KOT the kitchen has not seen. Seeding the set empty meant
  // every page load — and every reconnect that remounted the screen — treated the
  // whole standing board as new and chimed for it (bible §30). The first successful
  // read only records what is already there; so does the first read after a station
  // switch, which is a different board rather than a kitchen full of new KOTs.
  useEffect(() => {
    if (loading) return;
    // Compare against the station the tickets in hand were READ for, not the one
    // selected: the selection changes a render before its board arrives, and using
    // it seeded the old board under the new station's name — then chimed for every
    // ticket the new station really had.
    const seeded = announcedStation.current === boardStation;
    if (!seeded && error) return; // wait for a read that actually worked

    if (!seeded) announcedTickets.current = new Set();
    const fresh = tickets.filter(ticket => !announcedTickets.current.has(ticket.key));
    // Bookkeeping happens whether or not sound is on, so switching sound on later
    // does not chime for the board that was already standing.
    fresh.forEach(ticket => announcedTickets.current.add(ticket.key));

    if (!seeded) {
      announcedStation.current = boardStation;
      return;
    }
    if (!fresh.length) return;

    // Every new KOT is named on screen, sound or not, and as a system notification
    // when this screen is behind another app.
    fresh.forEach(ticket => {
      const words = `New KOT ${ticket.kot_number} · ${ticketTitle(ticket)}`;
      toast.info(words, { duration: 6000 });
      try {
        if (document.hidden && window.Notification?.permission === "granted") {
          new window.Notification(words, { tag: `kot-${ticket.key}` });
        }
      } catch {
        /* the on-screen notice above still shows */
      }
    });

    if (!activeSettings.kds_sound_enabled || !soundOn) return;

    try {
      const ctx = audioRef.current || new (window.AudioContext || window.webkitAudioContext)();
      audioRef.current = ctx;
      if (ctx.state === "suspended") {
        if (ctx.resume) ctx.resume();
        setSoundBlocked(true);
        return;
      }
      setSoundBlocked(false); // sound works again (a tap elsewhere unlocked it)
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = "sine";
      osc.frequency.value = 880;
      gain.gain.setValueAtTime(0.1, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.00001, ctx.currentTime + 0.5);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.5);
    } catch (e) {
      console.error("Audio playback failed", e);
      setSoundBlocked(true);
    }
    // `tickets` is re-sorted every render; `served` is the board as read.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [served, loading, error, boardStation, activeSettings.kds_sound_enabled, soundOn]);

  // A tap is what lets the browser play sound, so the button both switches the chime
  // and unlocks it, and asks once for system notifications.
  const onSound = () => {
    const next = soundBlocked ? true : !soundOn;
    if (next) {
      try {
        const ctx = audioRef.current || new (window.AudioContext || window.webkitAudioContext)();
        audioRef.current = ctx;
        if (ctx.resume) ctx.resume();
      } catch (e) {
        console.error("Audio unlock failed", e);
      }
      try {
        if (window.Notification?.permission === "default") window.Notification.requestPermission();
      } catch {
        /* not offered on this browser */
      }
    }
    setSoundBlocked(false);
    setSoundOn(next);
    saveChoice("kds.sound", next ? "on" : "off");
  };

  const urgency = minutes =>
    minutes >= activeSettings.kds_alert_minutes ? "critical" : minutes >= activeSettings.kds_warn_minutes ? "warning" : "fresh";

  const onBump = async ticket => {
    try {
      await bump(ticket);
      setLastBumped({ ticket, time: Date.now() });
      toast.success(`KOT ${ticket.kot_number} completed`);
    } catch (failure) {
      toast.error(failure?.response?.data?.detail || "Could not complete that KOT");
    }
  };

  const onRecall = async () => {
    if (!lastBumped) return toast.error("No completed order to recall");
    try {
      await recall(lastBumped.ticket);
      setLastBumped(null);
      toast.success("Last order recalled");
    } catch (failure) {
      toast.error(failure?.response?.data?.detail || "Recall window has expired");
    }
  };

  const recallTimeLeft = lastBumped ? Math.max(0, activeSettings.kds_recall_window_s - Math.floor((now - lastBumped.time) / 1000)) : 0;
  const isRecallDisabled = !lastBumped || recallTimeLeft <= 0;

  // The board wraps and scrolls, so nothing is unreachable — but a cook standing at
  // the pass must still be told there is more below the fold rather than guessing.
  const boardRef = useRef(null);
  const cardRefs = useRef(new Map());
  const [hiddenCount, setHiddenCount] = useState(0);

  const measureOverflow = useCallback(() => {
    const board = boardRef.current;
    if (!board) return;
    // Screen positions, not offsetTop: the board is not the cards' offsetParent, so
    // offsetTop counted from the page and the count drifted with the header height.
    const visibleBottom = board.getBoundingClientRect().bottom;
    let hidden = 0;
    cardRefs.current.forEach(node => {
      if (node && node.getBoundingClientRect().bottom > visibleBottom + 1) hidden += 1;
    });
    setHiddenCount(hidden);
  }, []);

  useEffect(() => {
    measureOverflow();
    window.addEventListener("resize", measureOverflow);
    return () => window.removeEventListener("resize", measureOverflow);
  }, [measureOverflow, tickets]);

  // One station of their own (Staff → Stations & stock): its name, no picker
  // (owner, 2026-10-31: the bar read "All stations" for a person limited to one).
  const stationName = station
    ? stations.find(s => s.id === station)?.name || "Station"
    : stations.length === 1 ? stations[0].name : "All stations";
  return (
    <div className="flex flex-col h-full bg-background text-foreground" data-testid="kitchen-display">
      {/*
        * ONE bar, not three.
        *
        * A 24px-padded masthead (eyebrow, a 30px h1 reading "Active kitchen orders"
        * on a screen whose whole existence is active kitchen orders, plus 48px
        * controls) sat on top of a second full-width band carrying the colour legend.
        * Together they took roughly 150px off the top of a wall-mounted screen — a
        * whole KOT card's worth of tickets pushed below the fold, on the one screen
        * in the product where below-the-fold means food goes cold (§2.2, §29).
        *
        * The name of the station, the ticket count, the legend and the two controls
        * are all still here, on one 48px row. Ticket typography below is untouched.
        */}
      <header className="flex flex-wrap items-center justify-between gap-x-loose gap-y-snug px-loose py-snug bg-card border-b border-border shadow-md shrink-0">
        {/* The station name never shrinks away: on a narrow bar the controls wrap below it. */}
        <div className="flex items-baseline gap-snug shrink-0">
          <strong className="text-title font-bold text-foreground truncate">
            {stationName}
          </strong>
          <span className="text-micro font-bold uppercase text-muted-foreground shrink-0">
            <span className="tabular-nums text-foreground" data-testid="active-kot-count">{tickets.length}</span> live
          </span>
        </div>

        {/* The legend is a key to the card colours, so it belongs beside them rather
            than on a band of its own. It drops out first when the bar runs short — below
            2xl since the sort and sound buttons joined the bar (2026-11-01). */}
        <div className="hidden 2xl:flex items-center gap-base text-meta font-bold uppercase shrink-0">
          <span className="flex items-center gap-tight text-success"><span className="w-2 h-2 rounded-full bg-success"></span>On time &lt; {activeSettings.kds_warn_minutes}m</span>
          <span className="flex items-center gap-tight text-warning"><span className="w-2 h-2 rounded-full bg-warning"></span>Attention {activeSettings.kds_warn_minutes}–{activeSettings.kds_alert_minutes}m</span>
          <span className="flex items-center gap-tight text-danger"><span className="w-2 h-2 rounded-full bg-danger"></span>Urgent &gt; {activeSettings.kds_alert_minutes}m</span>
        </div>

        <div className="flex items-center gap-snug shrink-0">
          {stations.length > 1 && (
            <select
              className="h-11 px-snug rounded-lg bg-muted border-border text-foreground text-body font-bold focus:ring-primary focus:border-primary"
              value={station ?? ""}
              onChange={event => setStation(event.target.value ? Number(event.target.value) : null)}
              data-testid="kds-station-select"
            >
              <option value="">All stations</option>
              {stations.map(item => (
                <option value={item.id} key={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          )}
          <Button
            variant="outline"
            className="h-11 px-base border-border bg-muted hover:bg-muted/70 text-foreground text-body font-bold uppercase"
            onClick={toggleSort}
            data-testid="kds-sort-button"
            aria-label={newestFirst ? "Newest KOT first, tap for oldest first" : "Oldest KOT first, tap for newest first"}
          >
            <ArrowDownUp className="w-4 h-4 lg:mr-snug" />
            <span className="hidden lg:inline">{newestFirst ? "Newest first" : "Oldest first"}</span>
          </Button>
          {activeSettings.kds_sound_enabled && (
            <Button
              variant="outline"
              className={`h-11 px-base text-body font-bold uppercase ${
                soundBlocked ? "border-warning bg-warning-subtle text-warning" : "border-border bg-muted hover:bg-muted/70 text-foreground"
              }`}
              onClick={onSound}
              data-testid="kds-sound-button"
              aria-label={soundBlocked ? "KOT sound is blocked, tap to turn it on" : soundOn ? "KOT sound on, tap to mute" : "KOT sound off, tap to turn on"}
            >
              {soundOn && !soundBlocked ? <Bell className="w-4 h-4 lg:mr-snug" /> : <BellOff className="w-4 h-4 lg:mr-snug" />}
              {/* A blocked chime keeps its words at every width: it is the one that needs a tap. */}
              <span className={soundBlocked ? "ml-snug lg:ml-0" : "hidden lg:inline"}>{soundBlocked ? "Tap for sound" : soundOn ? "Sound on" : "Sound off"}</span>
            </Button>
          )}
          <Button
            variant="outline"
            className="h-11 px-base border-border bg-muted hover:bg-muted/70 text-foreground text-body font-bold uppercase disabled:opacity-50"
            onClick={onRecall}
            disabled={isRecallDisabled}
            data-testid="recall-last-order-button"
          >
            <RotateCcw className="w-4 h-4 sm:mr-snug" aria-label="Recall" />
            <span className="hidden sm:inline">Recall</span> {recallTimeLeft > 0 ? `(${recallTimeLeft}s)` : ""}
          </Button>
        </div>
      </header>

      {error && (
        <div className="bg-danger-subtle text-danger border-b border-danger/30 px-loose py-snug flex items-center justify-center gap-snug text-body font-semibold shrink-0" role="alert" data-testid="kds-error">
          <TriangleAlert className="w-4 h-4 shrink-0" />
          <span>{error} — showing the last known queue.</span>
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-loose bg-background" ref={boardRef} onScroll={measureOverflow} data-testid="kds-board">
        {/* Every dish on the board added up across KOTs, so four separate dal
            tadkas read as one "4 Dal Tadka" (owner, 2026-10-25). The KOTs follow. */}
        {tickets.length > 0 && (
          <section className="mb-6 rounded-2xl border border-border bg-card p-4 transition-all" data-testid="kds-combined">
            <button
              type="button"
              onClick={toggleCombined}
              data-testid="kds-combined-toggle"
              aria-expanded={!combinedCollapsed}
              className="flex w-full items-center justify-between text-left focus:outline-none"
            >
              <div className="flex items-center gap-2">
                <h2 className="text-sm font-bold text-muted-foreground">All orders together</h2>
                <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-semibold text-muted-foreground">
                  {combinedLines.length} {combinedLines.length === 1 ? "dish" : "dishes"}
                </span>
              </div>
              <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground">
                <span>{combinedCollapsed ? "Expand" : "Collapse"}</span>
                <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${combinedCollapsed ? "-rotate-90" : "rotate-0"}`} />
              </div>
            </button>
            {!combinedCollapsed && (
              <div className="mt-3 flex flex-wrap gap-2">
                {combinedLines.map(line => (
                  <div key={line.key} className="flex items-center gap-3 rounded-xl border border-border bg-muted px-3 py-2">
                    <b className="text-2xl font-bold tabular-nums text-foreground">{fmtQty(line.quantity)}</b>
                    <span className="flex flex-col leading-tight">
                      <span className="text-lg font-bold text-foreground">{line.name}</span>
                      {(line.detail || line.note) && (
                        <span className="text-sm font-semibold text-warning">{[line.detail, line.note].filter(Boolean).join(" · ")}</span>
                      )}
                      {line.tickets > 1 && <span className="text-xs text-muted-foreground">{line.tickets} KOTs</span>}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}
        {tickets.length > 0 && (
          <div
            className="grid gap-6 items-start grid-cols-[repeat(auto-fill,minmax(min(100%,280px),1fr))]"
            data-testid="kot-card-grid"
          >
            {tickets.map(ticket => {
            const urgencyLevel = urgency(ticket.minutes);

            const secondsSince = ticket.kot_sent_at ? Math.max(0, now - Date.parse(ticket.kot_sent_at)) / 1000 : 0;
            const isNew = ticket.kot_sent_at && secondsSince < activeSettings.kds_new_ticket_flash_s;

            let cardClasses = "border-success/40 bg-card";
            let headerClasses = "bg-success-subtle border-success/30 text-foreground";
            let timerClasses = "text-success";
            let bumpClasses = "bg-action text-action-foreground hover:bg-action/90";

            if (urgencyLevel === "warning") {
              cardClasses = "border-warning/60 bg-card";
              headerClasses = "bg-warning-subtle border-warning/40 text-foreground";
              timerClasses = "text-warning font-bold";
              bumpClasses = "bg-action text-action-foreground hover:bg-action/90";
            } else if (urgencyLevel === "critical") {
              cardClasses = "border-danger bg-card ring-1 ring-danger";
              headerClasses = "bg-danger-subtle border-danger/50 text-foreground";
              timerClasses = "text-danger font-bold animate-pulse";
              bumpClasses = "bg-action text-action-foreground hover:bg-action/90";
            }

            if (isNew) {
              cardClasses += " animate-pulse ring-4 ring-info";
            }

            return (
              <article
                className={`flex flex-col rounded-2xl border-2 overflow-hidden ${cardClasses}`}
                key={ticket.key}
                data-testid={`kot-card-${ticket.key}`}
                ref={node => {
                  if (node) cardRefs.current.set(ticket.key, node);
                  else cardRefs.current.delete(ticket.key);
                }}
              >
                  <header className={`flex justify-between items-start p-4 border-b ${headerClasses}`}>
                    <div className="flex flex-col gap-1">
                        <small className="text-xs font-bold uppercase tracking-widest opacity-80">#{ticket.order_number} · KOT {ticket.kot_number}{ticket.captain_name ? ` · ${ticket.captain_name}` : ""}</small>
                        <strong className="text-2xl font-bold">{ticketTitle(ticket)}</strong>
                    </div>
                    <span className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-muted text-lg ${timerClasses}`} data-testid={`kot-${ticket.key}-elapsed`}>
                        <Clock3 className="w-5 h-5"/> {elapsedFromMinutes(ticket.minutes)}
                    </span>
                  </header>

                  <div className="flex items-center gap-2 px-4 py-2 bg-muted/50 border-b border-border text-muted-foreground font-bold uppercase tracking-wider text-sm">
                    {/* "DINE_IN" was the raw enum; the kitchen reads words (bible §12). */}
                    <UtensilsCrossed className="w-4 h-4"/> {String(ticket.order_type || "").replace(/_/g, " ")}
                  </div>

                  <div className="p-4 space-y-3">
                    {/* Cancelled items never reach this list — kds_service.list_tickets filters
                        `OrderItem.status != 'cancelled'`. Surfacing a cancelled-but-already-fired
                        item is a backend filter decision, not a styling branch here. */}
                    {ticket.items.map((item, index) => {
                      const isPreparing = item.status === "preparing";
                      const detail = itemDetail(item);
                      return (
                        <div
                          key={item.id}
                          data-testid={`kot-${ticket.key}-item-${index}`}
                          className={`flex items-start gap-4 p-3 rounded-xl border text-foreground ${isPreparing ? "bg-muted border-primary/50" : "bg-card border-border"}`}
                        >
                            <b className="text-xl font-bold w-11 h-11 flex items-center justify-center rounded-lg bg-background shrink-0 border border-border">{fmtQty(item.quantity)}</b>
                            <div className="flex flex-col pt-0.5 flex-1 min-w-0">
                                <strong className="text-xl font-bold leading-tight">{item.item_name}</strong>
                                {detail && <span className="text-base font-semibold text-muted-foreground mt-1">{detail}</span>}
                                {item.notes && (
                                  <span className="mt-1.5 border-l-4 border-primary/50 pl-2 text-base font-bold leading-snug" data-testid={`kot-${ticket.key}-item-${index}-note`}>
                                    <span className="text-xs uppercase tracking-widest opacity-70 mr-1">Note</span>
                                    {item.notes}
                                  </span>
                                )}
                                <span
                                  className="mt-2 text-xs font-bold uppercase tracking-widest text-muted-foreground"
                                  data-testid={`kot-${ticket.key}-item-${index}-state`}
                                >
                                  {isPreparing ? "Preparing" : "Queued"}
                                </span>
                            </div>
                        </div>
                      );
                    })}
                  </div>

                  <footer className="p-4 bg-card border-t border-border">
                    <Button className={`w-full h-16 text-xl font-bold uppercase tracking-wider shadow-lg ${bumpClasses}`} onClick={() => onBump(ticket)} data-testid={`kot-${ticket.key}-complete-button`}>
                        <CheckCircle2 className="w-6 h-6 mr-2" />
                        Bump / Complete
                    </Button>
                  </footer>
              </article>
            );
          })}
          </div>
        )}

        {hiddenCount > 0 && (
          // Zero height, so showing or hiding it never changes how far the board
          // scrolls. With its own height it vanished at the bottom, the board got
          // shorter and jumped, it came back, and the screen flickered (owner,
          // 2026-11-01: "KDS is glitching when scrolling to the bottom").
          <div className="sticky bottom-0 h-0 flex justify-center pointer-events-none" data-testid="kds-overflow-indicator">
            <span className="-translate-y-[calc(100%+0.75rem)] shadow-md flex items-center gap-2 rounded-full bg-card border border-border px-4 py-2 text-sm font-bold uppercase tracking-widest text-foreground">
              <ChevronDown className="w-4 h-4" />
              {hiddenCount} more below
            </span>
          </div>
        )}

        {loading && !tickets.length && (
          <div className="h-full flex items-center justify-center" data-testid="kds-loading-state">
            <div className="flex flex-col items-center text-muted-foreground max-w-md text-center p-8 border-2 border-dashed border-border rounded-3xl">
                <Loader2 className="w-16 h-16 mb-6 opacity-60 animate-spin" />
                <h2 className="text-2xl font-bold text-foreground mb-2">Loading the kitchen queue</h2>
                <p className="text-base font-medium">Reading fired KOTs from the server.</p>
            </div>
          </div>
        )}

        {!tickets.length && !loading && (
          <div className="h-full flex items-center justify-center" data-testid="kds-empty-state">
            <div className="flex flex-col items-center text-muted-foreground max-w-md text-center p-8 border-2 border-dashed border-border rounded-3xl">
                <History className="w-16 h-16 mb-6 opacity-50" />
                <h2 className="text-2xl font-bold text-muted-foreground mb-2">{stationName === "All stations" ? "Kitchen is all clear" : `${stationName} is all clear`}</h2>
                <p className="text-lg font-medium">New KOTs appear here as captains fire them.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
